import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { Dialog, DialogTitle, DialogDescription } from 'components/ui/dialog';
import { PublicDialogContent } from 'components/ui/public-dialog';
import { siteRequest } from 'lib/siteCustomerApi';
import { notifyWebsiteSessionChanged } from 'lib/siteAccessEvents';
import SignIn from './SignIn';
import { Notice } from './Shared';
import './AccessWorkspace.css';
import './NavigationAccount.css';

export default function NavigationAccount({ onOpen }) {
  const [state, setState] = useState({ loading: true });
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [logoutError, setLogoutError] = useState(null);
  const request = useRef(null), generation = useRef(0), holder = useRef(null), trigger = useRef(null);
  const location = useLocation(), navigate = useNavigate();
  const refresh = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController(), revision = ++generation.current;
    request.current = controller;
    try {
      const result = await siteRequest('/customer/profile', { signal: controller.signal });
      if (controller.signal.aborted || revision !== generation.current) return null;
      setState({ profile: result.data });
      return result.data;
    } catch (error) {
      if (!controller.signal.aborted && revision === generation.current)
        setState(error.status === 401 ? {} : { error });
      return null;
    }
  }, []);
  useEffect(() => {
    refresh();
    const verify = () => { if (document.visibilityState === 'visible') refresh(); };
    const ended = () => { generation.current++; request.current?.abort(); setState({}); };
    const changed = () => { setState({ loading: true }); refresh(); };
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('magellan-website-access') : null;
    if (channel) channel.onmessage = changed;
    window.addEventListener('focus', verify);
    window.addEventListener('pageshow', verify);
    window.addEventListener('site-access-ended', ended);
    window.addEventListener('site-access-changed', changed);
    return () => {
      request.current?.abort(); channel?.close();
      window.removeEventListener('focus', verify);
      window.removeEventListener('pageshow', verify);
      window.removeEventListener('site-access-ended', ended);
      window.removeEventListener('site-access-changed', changed);
    };
  }, [refresh]);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open || !state.profile) return undefined;
    const outside = event => { if (!holder.current?.contains(event.target)) setOpen(false); };
    const escape = event => { if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); } };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open, state.profile]);
  async function signedIn() {
    const profile = await refresh();
    if (!profile) throw new Error('Your session could not be verified. Please retry sign-in.');
    setOpen(false);
    notifyWebsiteSessionChanged();
    navigate('/customer/access');
  }
  async function logout() {
    if (signingOut) return;
    setSigningOut(true); setLogoutError(null);
    try {
      await siteRequest('/customer/logout', { method: 'POST', body: {} });
      setState({}); setOpen(false); notifyWebsiteSessionChanged();
    } catch (error) {
      if (error.status === 401) { setState({}); setOpen(false); notifyWebsiteSessionChanged(); }
      else setLogoutError(error);
    } finally { setSigningOut(false); }
  }
  return <div ref={holder} className="site-navigation-account">
    <button ref={trigger} type="button" className="site-navigation-profile" data-testid="nav-profile"
      aria-label={state.profile ? 'My account' : 'Sign in'} title="Profile"
      aria-expanded={open} aria-haspopup={state.profile ? undefined : 'dialog'}
      aria-controls={open && state.profile ? 'site-account-menu' : undefined}
      data-authenticated={Boolean(state.profile)}
      onClick={() => { onOpen?.(); setLogoutError(null); setOpen(value => !value); }}>
      <UserRound size={21} strokeWidth={1.5} aria-hidden="true" />
    </button>
    {open && state.profile && <div id="site-account-menu" className="site-account-menu public-account-menu">
      <p>Account</p>
      <Link to="/customer/access" onClick={() => setOpen(false)}>My downloads</Link>
      <Link to="/customer/access/profile" onClick={() => setOpen(false)}>My information</Link>
      <Link to="/cart/orders" onClick={() => setOpen(false)}>My orders</Link>
      <button type="button" disabled={signingOut} onClick={logout}>{signingOut ? 'Signing out…' : 'Sign out'}</button>
      <Notice error={logoutError} />
    </div>}
    <Dialog open={open && !state.profile} onOpenChange={setOpen}>
      <PublicDialogContent className="customer-access ca-login-dialog" onCloseAutoFocus={event => { event.preventDefault(); trigger.current?.focus(); }}>
        <DialogTitle className="sr-only">Sign in to Magellan</DialogTitle>
        <DialogDescription className="sr-only">Use the email associated with your purchases and downloads.</DialogDescription>
        {state.loading ? <p role="status">Checking your website session…</p> : <>
          <Notice error={state.error} />
          {state.error && <button type="button" onClick={refresh}>Retry website connection</button>}
          <SignIn compact onSignedIn={signedIn} />
        </>}
      </PublicDialogContent>
    </Dialog>
  </div>;
}
